/**
 * scan.ts — client helpers for the AI photo scan, ported from
 * artifacts/mobile/lib/scan.ts.
 *
 * Both scan modes share the identical server contract: POST /api/ai/scan-photo
 * with {imageUrl, category}, poll GET /api/jobs/:id. This file is orchestration
 * only — request one job, await it, hand back a typed suggestion. No new AI
 * endpoint, no change to the prompt.
 *
 * `category` is the seller's answer to "what am I photographing?" — the one
 * input a photo cannot supply. REQUIRED at this boundary and nullable; see
 * requestScan for why a null is stated rather than omitted.
 *
 * `supportImageUrl` is ONE further photo of the same item, and is required at
 * this boundary for exactly the same reason `category` is: a caller that
 * silently omits it buys the worse scan, and the omission looks identical to a
 * deliberate "there is no second photo". Stating the null is what separates
 * those two. The cap of one is the server's — scanPhoto's signature — not a
 * limit this file could enforce anyway.
 */
import type { AuthFetch } from "./importHub";
import { pollJobResult } from "./pollJob";
import type { JobPollTimeoutError } from "./pollJob";

/**
 * The apply contract — an explicitly-typed subset with no other keys, so the
 * client cannot widen what a server change might one day return. Mirrors
 * artifacts/api-server/src/lib/aiTools.ts's ScanPhotoResult field-for-field;
 * kept as an independent type here (not imported across the client/server
 * boundary) the same way the wire shapes in importHub.ts are.
 *
 * `description`, weight and dimensions are deliberately absent: the scan may
 * never populate a prose field, and weight is never pre-filled by anything in
 * this codebase.
 *
 * ── WHY `subcategory` AND `confidence` ARE HERE ─────────────────────────────
 *
 * They were dropped when this file was ported, and dropping them made an honest
 * pre-fill impossible — which is why they arrive now, with slice 4, rather than
 * earlier.
 *
 * `confidence` is the model's own 0-100 claim that `title` names the EXACT
 * item. Without it a client cannot tell a graded slab read at 95 from an
 * unidentifiable garment read at 5, and the server hands back a title and a
 * dollar value for both — so pre-filling without it means pre-filling a
 * confident fiction. scanConfidence.ts holds the two bars that read it.
 *
 * `subcategory` is, on the web wizard's path, the ONLY classification answer
 * the model is asked for: the seller names the parent before the shot, so
 * aiTools.ts tells the model to take that as given and drops any `category` it
 * returns anyway. Which also means —
 *
 * ⚠️ `category` IS ALWAYS `undefined` ON THE WIZARD PATH, and that is not a
 * bug to fix here. aiTools.ts keys the drop on `sellerNamedParent`, and the
 * wizard always names one. The field stays because the folder-drop path
 * (importPipeline) genuinely sends none, and there the model does answer.
 */
export interface ScanSuggestion {
  title?: string;
  brand?: string;
  category?: string;
  subcategory?: string;
  suggestedValue?: number;
  condition?: string;
  confidence?: number;
}

/**
 * What requestScan throws. The HTTP status rides on the error so a caller can
 * tell a 429 from any other failure WITHOUT matching on message text — the
 * rate-limited case has its own honest recovery (the photo is kept, the flow
 * degrades to ordinary manual entry), and that branch must not hinge on the
 * wording of a server string.
 */
export interface ScanRequestError extends Error {
  status?: number;
}

/**
 * POST /api/ai/scan-photo — enqueues the scan, returns the job id to poll.
 *
 * `category` is the parent slug the SELLER named before the shot — the one
 * thing on this request a photo cannot supply. It narrows the prompt and is
 * handed to the catalog seam (api-server/src/lib/aiTools.ts's scanPhoto).
 *
 * 🔑 SENT ALWAYS, EVEN AS null. The server reads `category` off the body and
 * treats absent as "the model gets the parent list and guesses" — an honest
 * degradation that exists for app builds already on phones, NOT a default this
 * new surface should opt into. Web asks the question before the scan, so it
 * always has an answer; making the parameter required at this boundary is what
 * stops a caller quietly omitting it and silently buying the worse scan.
 * Matches mobile's `requestScan(imageUrl, category)` field for field, plus
 * `supportImageUrl` — which mobile does NOT send today. The server treats it
 * as optional, so mobile keeps working unchanged on one photo; wiring a second
 * angle there is its own change with its own device walk.
 */
export async function requestScan(
  authFetch: AuthFetch,
  imageUrl: string,
  category: string | null,
  supportImageUrl: string | null,
): Promise<string> {
  let res: Response;
  try {
    res = await authFetch("/api/ai/scan-photo", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl, category, supportImageUrl }),
    });
  } catch {
    throw new Error("Could not start the scan. Please try again.") as ScanRequestError;
  }
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(
      (data as { error?: string }).error || "Could not start the scan. Please try again.",
    ) as ScanRequestError;
    err.status = res.status;
    throw err;
  }
  return (data as { jobId: string }).jobId;
}

/**
 * Poll the scan job to completion and reconstruct the result into exactly the
 * typed ScanSuggestion field set.
 *
 * 🔑 RECONSTRUCTED EXPLICITLY — never `return raw as ScanSuggestion` and never
 * a spread. The server already rebuilds scanPhoto()'s return from an explicit
 * typed field set (aiTools.ts), so today's job result already carries nothing
 * extra — but this reconstruction is what keeps that true on the client even if
 * a future server change widens the job payload.
 */
export async function awaitScan(
  authFetch: AuthFetch,
  jobId: string,
  opts?: { intervalMs?: number; maxAttempts?: number },
): Promise<ScanSuggestion> {
  const raw = await pollJobResult<{
    title?: string;
    brand?: string;
    category?: string;
    subcategory?: string;
    suggestedValue?: number;
    condition?: string;
    confidence?: number;
  }>(authFetch, jobId, opts);
  const { title, brand, category, subcategory, suggestedValue, condition, confidence } = raw;
  return { title, brand, category, subcategory, suggestedValue, condition, confidence };
}

/**
 * Every way a scan can end, as one closed set. There is no sixth "scan failed"
 * state, because a photo with no typed fields is a form the seller fills in,
 * which is what the review screen already is.
 *
 * `unresolved` is the load-bearing one. It means THIS POLL gave up, not that
 * the job did: the job may still be running server-side, and its answer is
 * already paid for. It therefore carries the jobId, and the only thing offered
 * on it is resuming that same job. A fresh scan is offered only on `failed`,
 * where there is genuinely nothing left to wait for — which is the structural
 * reason a timeout can never double-charge for one photo or buy a second,
 * different answer to it.
 */
export type ScanOutcome =
  | { status: "done"; suggestion: ScanSuggestion }
  | { status: "unresolved"; jobId: string }
  | { status: "failed" }
  | { status: "rateLimited" }
  | { status: "error" };

/** pollJobResult marks its own ceiling-reached error; see JobPollTimeoutError. */
function isPollTimeout(err: unknown): boolean {
  return err instanceof Error && (err as Partial<JobPollTimeoutError>).timedOut === true;
}

/**
 * Did that poll ever actually READ the job?
 *
 * pollJobResult's `if (!res.ok) continue` makes a 401 or a 404 indistinguish-
 * able from "still processing", so a job this client can never see exhausts the
 * ceiling and arrives here wearing a timeout's clothes. Classified as
 * `unresolved`, it offered "Keep waiting" forever with no transition to
 * anything else — a dead end with no exit (PR #848 review, F3). A ceiling
 * exhausted with ZERO readable responses is a dead job, not a slow one.
 *
 * ⚠ FAILS TOWARD DEAD when the marker is absent, and that direction is
 * deliberate. Getting it wrong one way offers a new scan for a job that was
 * merely slow — $0.0008 and a working way forward. Getting it wrong the other
 * way is the trap this fix removes.
 */
function sawTheJob(err: unknown): boolean {
  return (err as Partial<JobPollTimeoutError>).sawReadableResponse === true;
}

/**
 * Poll an EXISTING scan job to an outcome. Makes NO request to
 * /api/ai/scan-photo — it does not import, reference or reach anything that
 * could: the only call in its body is the poll. That is the whole guarantee
 * ("a resumed poll makes zero additional POST /scan-photo calls"), and it is
 * structural rather than conventional, so no future edit can quietly turn a
 * resume into a re-POST without moving code into this function that visibly
 * does not belong here.
 */
export async function resumeScanJob(
  authFetch: AuthFetch,
  jobId: string,
  opts?: { intervalMs?: number; maxAttempts?: number },
): Promise<ScanOutcome> {
  try {
    return { status: "done", suggestion: await awaitScan(authFetch, jobId, opts) };
  } catch (err) {
    // A ceiling reached is not a job that died — see JobPollTimeoutError —
    // UNLESS the poll never managed to read the job even once, in which case it
    // is not slow, it is unreachable. See sawTheJob.
    if (!isPollTimeout(err)) return { status: "failed" };
    return sawTheJob(err) ? { status: "unresolved", jobId } : { status: "failed" };
  }
}

/**
 * Start a NEW scan: exactly one POST, then the same poll a resume does.
 *
 * A 429 gets its own outcome rather than being folded into `error`, because it
 * has a different honest recovery: nothing is wrong with the photo or the app,
 * the seller has simply run a lot of scans, and the flow degrades to ordinary
 * manual entry with the photo still attached.
 */
export async function startScanJob(
  authFetch: AuthFetch,
  imageUrl: string,
  category: string | null,
  supportImageUrl: string | null,
  opts?: { intervalMs?: number; maxAttempts?: number },
): Promise<ScanOutcome> {
  let jobId: string;
  try {
    jobId = await requestScan(authFetch, imageUrl, category, supportImageUrl);
  } catch (err) {
    const status = (err as Partial<ScanRequestError> | null)?.status;
    return { status: status === 429 ? "rateLimited" : "error" };
  }
  return resumeScanJob(authFetch, jobId, opts);
}
